import React, { useCallback, useState, useEffect, useRef } from "react";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { generateId } from "../../utils/Utils";
import { Note } from "../../types";
import { useTranslation } from "react-i18next";
import { Button } from "../button/Button";
import { NoteEditor } from "./note-editor/NoteEditor";
import { NoteFormattingToolbar } from "./note-formatting-toolbar/NoteFormattingToolbar";
import { useNoteTableControls } from "./note-table-controls/useNoteTableControls";
import { getNotePlainText, noteContentToHtml, sanitizeNoteHtml } from "../../utils/noteFormatting";
import "./NoteModal.css";

interface NoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (note: Note) => void;
    editElement?: Note;
    onUpdate: (note: Note) => void;
}

export const NoteModal: React.FC<NoteModalProps> = ({
    isOpen,
    onClose,
    onAdd,
    editElement,
    onUpdate,
}) => {
    const { t } = useTranslation();
    const isEditing = !!editElement;
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [viewportStyle, setViewportStyle] = useState<React.CSSProperties>({});
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const viewport = window.visualViewport;
        const previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const updateViewport = () => {
            if (!viewport) {
                setViewportStyle({});
                return;
            }

            setViewportStyle({
                top: `${viewport.offsetTop}px`,
                left: `${viewport.offsetLeft}px`,
                right: 'auto',
                bottom: 'auto',
                width: `${viewport.width}px`,
                height: `${viewport.height}px`,
            });
        };

        updateViewport();
        viewport?.addEventListener('resize', updateViewport);
        viewport?.addEventListener('scroll', updateViewport);

        return () => {
            viewport?.removeEventListener('resize', updateViewport);
            viewport?.removeEventListener('scroll', updateViewport);
            document.body.style.overflow = previousBodyOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && editElement) {
            setTitle(editElement.title || "");
            setContent(noteContentToHtml(editElement.content || ""));
        } else if (isOpen) {
            setTitle("");
            setContent("");
        }
    }, [isOpen, editElement]);

    const handleTitleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            contentRef.current?.focus();
        }
    };

    useEffect(() => {
        if (!isOpen || !contentRef.current) return;
        if (contentRef.current.innerHTML !== content) {
            contentRef.current.innerHTML = content;
        }
    }, [content, isOpen]);

    const handleContentChange = useCallback(() => {
        if (!contentRef.current) return;
        contentRef.current
            .querySelectorAll('ul[data-checklist="true"] ul')
            .forEach((list) => list.setAttribute('data-checklist', 'true'));

        contentRef.current
            .querySelectorAll('ul[data-checklist="true"] li')
            .forEach((item) => {
                if (!item.getAttribute('data-checked')) {
                    item.setAttribute('data-checked', 'false');
                }
            });
        setContent(sanitizeNoteHtml(contentRef.current.innerHTML));
    }, []);

    const {
        contentAreaRef,
        tableControls,
        updateTableControls,
        runTableAction,
    } = useNoteTableControls({
        editorRef: contentRef,
        onContentChange: handleContentChange,
        isOpen,
    });

    const handleContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        const item = target.closest('li');
        const editor = contentRef.current;

        if (!editor) return;
        if (!item || !editor.contains(item) || !item.closest('ul[data-checklist="true"]')) {
            requestAnimationFrame(() => updateTableControls(null));
            return;
        }

        const rect = item.getBoundingClientRect();
        const clickedCheckboxArea = event.clientX - rect.left <= 24;
        if (!clickedCheckboxArea) {
            requestAnimationFrame(() => updateTableControls(null));
            return;
        }

        event.preventDefault();
        const isChecked = item.getAttribute('data-checked') === 'true';
        item.setAttribute('data-checked', isChecked ? 'false' : 'true');
        handleContentChange();
    };

    const handleSubmit = () => {
        const sanitizedContent = sanitizeNoteHtml(content);
        if (!getNotePlainText(sanitizedContent).trim()) return;

        if (isEditing && editElement) {
            onUpdate({
                ...editElement,
                title: title.trim() || t("noteModal.untitledNote"),
                content: sanitizedContent,
                lastUpdated: new Date().toISOString(),
                edit: false,
            });
        } else {
            const newNote: Note = {
                id: generateId(),
                type: "note",
                title: title.trim() || t("noteModal.untitledNote"),
                content: sanitizedContent,
                lastUpdated: new Date().toISOString(),
            };
            onAdd(newNote);
        }

        setTitle("");
        setContent("");
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="note-modal-overlay"
            style={viewportStyle}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <div className="note-modal" onClick={(e) => e.stopPropagation()}>
                <div className="note-modal-toolbar">
                    <div className="note-modal-actions">
                        <Button
                            onClick={onClose}
                            type={"button"}
                            status={"secondary"}
                            text={""}
                            icon={faTimes}
                            ariaLabel={t("noteModal.cancel")}
                        />
                        <Button
                            onClick={handleSubmit}
                            type={"button"}
                            status={"primary"}
                            text={""}
                            icon={faCheck}
                            ariaLabel={t("noteModal.save")}
                        />
                    </div>
                </div>
                <NoteEditor
                    title={title}
                    contentRef={contentRef}
                    contentAreaRef={contentAreaRef}
                    tableControls={tableControls}
                    onTitleChange={setTitle}
                    onTitleKeyDown={handleTitleKeyDown}
                    onContentChange={handleContentChange}
                    onContentClick={handleContentClick}
                    onUpdateTableControls={updateTableControls}
                    onTableAction={runTableAction}
                />
                <NoteFormattingToolbar
                    editorRef={contentRef}
                    onContentChange={handleContentChange}
                />
            </div>
        </div>
    );
};
